import type {FollowerApiModel} from '../../../api/follower';
import type {ApiListResponse} from '../../../api/types';
import type {
  CoreClientFollowersListParams,
  CoreCoachFollowersListParams,
} from '../../../api/types/core/methods';
import type {AsyncStrategy} from '../utils';

import {apiRequest} from '../../../api/utils/apiRequest';
import {isIamCoach} from '../../../utils/entity/isIamCoach';
import {logger} from '../../../utils/logger';

type Config = {
  userId: string;
};

const strategy: AsyncStrategy = async (roles, config) => {
  if (isIamCoach()) {
    return roles.coach(config);
  } else {
    return roles.client(config);
  }
};

export async function findFollowerByUserId(config: Config) {
  return strategy({client: findFollower, coach: findFollower}, config);
}

findFollowerByUserId.asClient = findFollower;
findFollowerByUserId.asCoach = findFollower;

async function fetchClientFollowers(
  config: Config,
): Promise<ApiListResponse<FollowerApiModel>> {
  try {
    return apiRequest<CoreClientFollowersListParams>({
      method: 'client.followers.list',
      params: {
        query: [['coach_id', '==', config.userId]],
      },
    });
  } catch (e) {
    logger.error(e);

    throw e;
  }
}

async function fetchCoachFollowers(
  config: Config,
): Promise<ApiListResponse<FollowerApiModel>> {
  try {
    return apiRequest<CoreCoachFollowersListParams>({
      method: 'coach.followers.list',
      params: {
        query: [['client_id', '==', config.userId]],
      },
    });
  } catch (e) {
    logger.error(e);

    throw e;
  }
}

async function findFollower(config: Config) {
  try {
    const result = isIamCoach()
      ? await fetchCoachFollowers(config)
      : await fetchClientFollowers(config);

    if (!result._items.length) {
      return null;
    }

    const [follower] = result._items;

    return follower;
  } catch (e) {
    logger.error(e);

    throw e;
  }
}
